import React, { useState, useEffect } from 'react';
import styles from '../assets/dataTable.module.css';

const DataTable = ({
  columns,
  data,
  pagination,
  onPageChange,
  showSearch = true,
  showEntries = true,
  entriesOptions = [10, 25, 50, 100],
  defaultEntries = 25,
  serverSide = false, // When true, use pagination prop and don't slice data
}) => {
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(defaultEntries);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState(data);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Use server-side pagination values if provided
  const isServerSide = serverSide || (pagination && pagination.totalRecords !== undefined);
  const currentPage = isServerSide ? (pagination?.currentPage || 1) : localCurrentPage;
  const totalRecords = isServerSide ? (pagination?.totalRecords || 0) : filteredData.length;
  const totalPages = isServerSide
    ? (pagination?.totalPages || 1)
    : Math.ceil(filteredData.length / entriesPerPage);
  const recordsPerPage = isServerSide
    ? (pagination?.recordsPerPage || 30)
    : entriesPerPage;

  useEffect(() => {
    if (isServerSide) {
      // For server-side, just use data as-is (already filtered/paginated by server)
      setFilteredData(data);
      return;
    }

    let filtered = data;
    if (searchTerm) {
      filtered = data.filter((item) =>
        Object.values(item).some((value) => {
          // Skip objects/arrays to avoid [object Object] comparisons
          if (value === null || value === undefined) return false;
          if (typeof value === "object") return false;
          return String(value).toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;

        // Compare values
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredData(filtered);
    setLocalCurrentPage(1);
  }, [data, searchTerm, sortConfig, isServerSide]);

  // For client-side, slice the data; for server-side, use all data (already paginated)
  const startIndex = isServerSide
    ? ((currentPage - 1) * recordsPerPage)
    : ((localCurrentPage - 1) * entriesPerPage);
  const endIndex = isServerSide
    ? (startIndex + data.length)
    : (startIndex + entriesPerPage);
  const currentData = isServerSide
    ? filteredData
    : filteredData.slice((localCurrentPage - 1) * entriesPerPage, localCurrentPage * entriesPerPage);

  const handlePageChange = (page) => {
    if (isServerSide) {
      // For server-side, just call the callback
      if (onPageChange) {
        onPageChange(page);
      }
    } else {
      setLocalCurrentPage(page);
      if (onPageChange) {
        onPageChange(page);
      }
    }
  };

  const handleEntriesChange = (event) => {
    const newEntries = parseInt(event.target.value);
    setEntriesPerPage(newEntries);
    setLocalCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSort = (accessor) => {
    let direction = 'asc';
    if (sortConfig.key === accessor && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: accessor, direction });
  };

  const getSortIcon = (accessor) => {
    if (sortConfig.key !== accessor) {
      return '⇅';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const pagesTotal = totalPages;

    if (pagesTotal <= maxVisiblePages) {
      for (let i = 1; i <= pagesTotal; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(pagesTotal);
      } else if (currentPage >= pagesTotal - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = pagesTotal - 3; i <= pagesTotal; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(pagesTotal);
      }
    }

    return pages;
  };

  return (
    <div className={styles.dataTableContainer}>
      <div className={styles.dtToolbar}>
        {showEntries && (
          <div className={styles.entriesControl}>
            <label>
              Show{' '}
              <select value={entriesPerPage} onChange={handleEntriesChange}>
                {entriesOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        )}

        {showSearch && (
          <div className={styles.searchControl}>
            <label>
              Search:{' '}
              <input
                type="search"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search..."
              />
            </label>
          </div>
        )}
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={styles.tableHeader}
                  onClick={() => column.accessor && handleSort(column.accessor)}
                  style={{ cursor: column.accessor ? 'pointer' : 'default' }}
                >
                  {column.Header}
                  {column.accessor && (
                    <span style={{ marginLeft: '4px', fontSize: '12px' }}>
                      {getSortIcon(column.accessor)}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.map((row, rowIndex) => (
              <tr key={rowIndex} className={styles.tableRow}>
                {columns.map((column, colIndex) => {
                  const cellValue = row[column.accessor];
                  // Safely render cell value - convert objects to string
                  const safeValue = (val) => {
                    if (val === null || val === undefined) return "-";
                    if (typeof val === "object") return JSON.stringify(val);
                    return val;
                  };
                  return (
                    <td key={colIndex} className={styles.tableCell}>
                      {column.Cell
                        ? column.Cell({
                            value: cellValue,
                            row: { original: row },
                          })
                        : safeValue(cellValue)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <div className={styles.paginationInfo}>
          Showing {totalRecords === 0 ? 0 : startIndex + 1} to {Math.min(startIndex + currentData.length, totalRecords)}{' '}
          of {totalRecords} entries
        </div>

        <div className={styles.paginationControls}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={styles.pageButton}
          >
            Previous
          </button>

          {getPageNumbers().map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' && handlePageChange(page)}
              disabled={page === '...'}
              className={`${styles.pageButton} ${
                page === currentPage ? styles.activePage : ''
              } ${page === '...' ? styles.ellipsis : ''}`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
            className={styles.pageButton}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
